/**
 * Stacks — `/countries/:cc/admin/stacks`. The project stack vocabulary's authoring
 * screen (migration 39, owner decision D-2b; API cluster C4).
 *
 * AUTHORED (no Figma exists for the admin surface); `PlatformsPage`'s shape minus
 * everything D-2b removed, and the removals are the ruling rather than thrift:
 *
 *   · NO applies-to controls. A stack has no applicability question — there is no
 *     such thing as a CRF's stack, and the DATABASE enforces it
 *     (`ck_crf_stacks_project`; `stack.dto.ts:9-13`). Checkboxes here would offer a
 *     choice the write refuses.
 *   · NO country column and NO availability screen. `/countries/:cc/stacks` does not
 *     exist and must not (D-2b: a stack is what our engineers build in, not what a
 *     country sells — `stacks.controller.ts:9-20`, absence pinned by
 *     `stacks.e2e-spec.ts` suite (a)). Inventing a per-country flag on this screen
 *     would be the second dimension nobody asked for.
 *
 * ONE attach count, not two — a stack cannot legally attach to a CRF, so a CRF
 * column would read as a bug the day it printed 0 forever (`stacks.service.ts:73-77`).
 *
 * THE GATE is `config.global` ON THE READ, exactly as `PlatformsPage` documents it:
 * every route including the list runs `assertGlobalConfigAccess`
 * (`stacks.controller.ts:58-63`), so the screen refuses — absent, not disabled —
 * without group admin or `config.global` held somewhere.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SkeletonRows,
  TextField,
} from '../../components/ui';
import { useSession } from '../../auth/session';
import { ApiRequestError } from '../../lib/api';
import { createStack, fetchAdminStacks, patchStack, vocabularyKeys } from './vocabulary-api';
import type { StackAdminRow } from './vocabulary-api';
import { ConfirmDialog } from './ConfirmDialog';
import { errorSentence } from './states';
import { NoGlobalConfigAccess } from './vocabulary-states';

/** The API's own sentence when it carries one — the NetworkRules pattern. */
function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

export function StacksPage(): React.JSX.Element {
  const { can, isGroupAdmin, accessibleCountries } = useSession();
  const queryClient = useQueryClient();

  /** "Held ANYWHERE" — the `assertGlobalConfigAccess` shape; see `PlatformsPage`. */
  const permitted = isGroupAdmin || accessibleCountries.some((code) => can(code, 'config.global'));

  const [name, setName] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [retiring, setRetiring] = useState<StackAdminRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const stacks = useQuery({
    queryKey: vocabularyKeys.stacks(),
    queryFn: ({ signal }) => fetchAdminStacks(signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: vocabularyKeys.stacks() });
  };

  const create = useMutation({
    mutationFn: () => createStack({ name: name.trim() }),
    onSuccess: () => {
      setName('');
      setActionError(null);
      invalidate();
    },
    onError: (error) => setActionError(apiSentence(error, 'The stack was not added.')),
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; body: { name?: string; sort_order?: number; active?: boolean } }) =>
      patchStack(input.id, input.body),
    onSuccess: () => {
      setRetiring(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      setRetiring(null);
      setActionError(apiSentence(error, 'The change was not saved.'));
    },
  });

  if (!permitted) return <NoGlobalConfigAccess />;

  const rows = stacks.data ?? [];
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Stacks"
        subtitle="What our engineers build projects in — one list for the whole group. A stack is not a country decision: there is no per-country switch, deliberately (D-2b)."
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        <Card title="Add a stack">
          <form
            className="flex flex-wrap items-start gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() === '' || create.isPending) return;
              create.mutate();
            }}
          >
            <div className="w-full max-w-sm">
              <TextField
                label="Name"
                value={name}
                maxLength={60}
                placeholder="e.g. Flutter"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            {/* mt-[26px] aligns with the field boxes — the NetworkRules measurement. */}
            <Button type="submit" className="mt-[26px]" disabled={name.trim() === '' || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add stack'}
            </Button>
          </form>
          <p className="text-label text-fg-secondary">
            Projects pick from this list before they leave draft. Names are unique
            group-wide; a duplicate is refused naming the row that already holds it.
          </p>
        </Card>

        <Card title="Stack vocabulary">
          {stacks.isPending ? (
            <SkeletonRows rows={3} label="Loading stacks" />
          ) : stacks.isError ? (
            /* NOT an empty vocabulary: "could not be read" is not "nothing is authored". */
            <Alert tone="danger" title="The stack vocabulary could not be read">
              {errorSentence(stacks.error, 'The server did not answer.')} Nothing below is a
              count of what is authored — this is a read failure, not an empty register.
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Layers aria-hidden="true" className="size-5" />}
              title="No stacks authored yet"
              description="Add the first value above. Until one exists, project confirmation refuses with 'no stack has been authored in this system yet' — this screen is where that refusal is cleared."
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">Name</th>
                  <th scope="col" className="py-2 pr-3 font-normal">In use</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Status</th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <StackRow
                    key={row.id}
                    row={row}
                    selected={row.id === selectedId}
                    onEdit={() => {
                      setActionError(null);
                      setSelectedId(row.id === selectedId ? null : row.id);
                    }}
                    onRetire={() => {
                      setActionError(null);
                      setRetiring(row);
                    }}
                    onReactivate={() => {
                      setActionError(null);
                      patch.mutate({ id: row.id, body: { active: true } });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {selected === null ? null : (
          <StackEditorCard
            key={selected.id}
            row={selected}
            busy={patch.isPending}
            onSave={(body) => patch.mutate({ id: selected.id, body })}
          />
        )}
      </div>

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire '${retiring?.name ?? ''}'?`}
        body={
          'The value leaves the project picker in every country immediately. Projects ' +
          'that already carry it keep it, readable, and it stays listed here as the ' +
          'audit record — retirement can be undone with Reactivate.'
        }
        confirmLabel="Retire stack"
        busy={patch.isPending}
        onConfirm={() => {
          if (retiring !== null) patch.mutate({ id: retiring.id, body: { active: false } });
        }}
        onCancel={() => setRetiring(null)}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── table row ── */

function StackRow({
  row,
  selected,
  onEdit,
  onRetire,
  onReactivate,
}: {
  row: StackAdminRow;
  selected: boolean;
  onEdit: () => void;
  onRetire: () => void;
  onReactivate: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      <td className={`py-2.5 pr-3 text-body ${row.active ? 'text-fg' : 'text-fg-secondary'}`}>
        {row.name}
      </td>
      {/* ONE count — projects only; a stack cannot attach to a CRF (D-2b screen note). */}
      <td className="tnum py-2.5 pr-3 text-body13 text-fg-secondary">
        {row.attached === 0 ? '—' : `${row.attached} project${row.attached === 1 ? '' : 's'}`}
      </td>
      <td className="py-2.5 pr-3">
        {row.active ? null : (
          /* Bordered, unfilled — the agent-keys/network-rules AA measurement. */
          <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
            Retired
          </span>
        )}
      </td>
      <td className="py-2.5 text-right">
        <span className="inline-flex items-center gap-1">
          <Button variant="quiet" onClick={onEdit}>
            {selected ? 'Close' : 'Edit'}
          </Button>
          {row.active ? (
            <Button variant="quiet" onClick={onRetire}>
              Retire…
            </Button>
          ) : (
            <Button variant="quiet" onClick={onReactivate}>
              Reactivate
            </Button>
          )}
        </span>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────────────────────── editor card ── */

function StackEditorCard({
  row,
  busy,
  onSave,
}: {
  row: StackAdminRow;
  busy: boolean;
  onSave: (body: { name?: string; sort_order?: number }) => void;
}): React.JSX.Element {
  const [name, setName] = useState(row.name);
  const [sortOrder, setSortOrder] = useState(String(row.sort_order));

  const parsedSort = /^\d+$/.test(sortOrder.trim()) ? Number(sortOrder.trim()) : null;

  /** Only what CHANGED goes on the wire — `UpdateStackDto`: absent means untouched. */
  const body: { name?: string; sort_order?: number } = {};
  if (name.trim() !== row.name) body.name = name.trim();
  if (parsedSort !== null && parsedSort !== row.sort_order) body.sort_order = parsedSort;

  const savable = name.trim() !== '' && parsedSort !== null && Object.keys(body).length > 0;

  return (
    <Card title={`Edit '${row.name}'`}>
      <form
        className="flex flex-wrap items-start gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!savable || busy) return;
          onSave(body);
        }}
      >
        <div className="w-full max-w-sm">
          <TextField
            label="Name"
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="w-28">
          <TextField
            label="Sort order"
            inputMode="numeric"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
        <Button type="submit" className="mt-[26px]" disabled={!savable || busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
      <p className="text-label text-fg-secondary">
        Renaming changes the label on every project that carries this value. Order is
        explicit business order, not alphabetical — the product-groups convention.
      </p>
    </Card>
  );
}
